// Carrousel SNK : piste à défilement natif (scroll-snap), flèches et points.
// Les flèches peuvent être n'importe où dans la page : [data-snk-slider-prev="ID"] / [data-snk-slider-next="ID"].
// Options dans le carrousel : points [data-snk-dots], barre de progression [data-snk-progress], compteur [data-snk-counter].
// Au doigt : défilement natif avec inertie et aimantation. À la souris : glisser-déposer, puis aimantation douce
// sur la carte la plus proche dans le sens du geste ; le clic qui suit un glisser est ignoré.
if (!customElements.get('snk-slider')) {
  customElements.define(
    'snk-slider',
    class SnkSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-snk-track]');
        this.dots = this.querySelector('[data-snk-dots]');
        this.progress = this.querySelector('[data-snk-progress] i');
        this.counter = this.querySelector('[data-snk-counter]');
        this.onScroll = () => requestAnimationFrame(() => this.update());
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
        this.bindButtons();
        this.bindDrag();
        this.setupAutoplay();
        this.update();
      }

      // Avance automatique [data-snk-autoplay="secondes"] : une carte à la fois, retour au début après la
      // dernière. En pause au survol, au focus et hors de l'écran ; arrêt définitif dès que le visiteur touche
      // au carrousel. Jamais si prefers-reduced-motion.
      setupAutoplay() {
        const seconds = Number(this.dataset.snkAutoplay);
        if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        let visible = false;
        let hovered = false;
        const tick = () => {
          if (!visible || hovered || document.hidden) return;
          const { scrollLeft, scrollWidth, clientWidth } = this.track;
          if (scrollLeft + clientWidth >= scrollWidth - 2) this.track.scrollTo({ left: 0, behavior: 'smooth' });
          else this.track.scrollTo({ left: this.nextStop(1), behavior: 'smooth' });
        };
        this.autoplayTimer = setInterval(tick, seconds * 1000);
        const stop = () => this.stopAutoplay();
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) =>
          this.track.addEventListener(type, stop, { once: true, passive: true })
        );
        this.addEventListener('pointerenter', () => (hovered = true));
        this.addEventListener('pointerleave', () => (hovered = false));
        this.addEventListener('focusin', () => (hovered = true));
        this.addEventListener('focusout', () => (hovered = false));
        this.visibilityObserver = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), { threshold: 0.3 });
        this.visibilityObserver.observe(this);
      }

      stopAutoplay() {
        clearInterval(this.autoplayTimer);
        this.visibilityObserver?.disconnect();
      }

      // Position de la carte suivante (1) ou précédente (-1) par rapport au défilement actuel
      nextStop(direction) {
        const track = this.track;
        const padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
        const origin = track.getBoundingClientRect().left + padding;
        const current = track.scrollLeft;
        const stops = [...track.children].map((item) => current + item.getBoundingClientRect().left - origin);
        const max = track.scrollWidth - track.clientWidth;
        const target =
          direction > 0 ? stops.find((stop) => stop > current + 2) : [...stops].reverse().find((stop) => stop < current - 2);
        return Math.min(max, Math.max(0, target ?? current));
      }

      bindDrag() {
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
        this.track.addEventListener(
          'click',
          (event) => {
            if (!this.dragged) return;
            event.preventDefault();
            event.stopPropagation();
          },
          { capture: true }
        );
        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (this.track.scrollWidth <= this.track.clientWidth) return;
          const startX = event.clientX;
          const startLeft = this.track.scrollLeft;
          this.dragged = false;
          const move = (moveEvent) => {
            const delta = moveEvent.clientX - startX;
            if (!this.dragged && Math.abs(delta) < 6) return;
            this.dragged = true;
            this.track.classList.add('is-dragging');
            this.track.scrollLeft = startLeft - delta;
          };
          const up = (upEvent) => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            if (!this.dragged) return;
            this.settle(Math.sign(startX - upEvent.clientX));
            setTimeout(() => (this.dragged = false));
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        });
      }

      // Fin d'un glisser à la souris : la carte suivante (ou précédente) vient se caler en douceur
      settle(direction) {
        const track = this.track;
        const current = track.scrollLeft;
        const target = this.nextStop(direction);
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        const release = () => track.classList.remove('is-dragging');
        if ('onscrollend' in window && behavior === 'smooth' && Math.abs(target - current) > 1) {
          track.addEventListener('scrollend', release, { once: true });
        } else {
          setTimeout(release, 500);
        }
        track.scrollTo({ left: target, behavior });
      }

      disconnectedCallback() {
        this.track.removeEventListener('scroll', this.onScroll);
        this.resizeObserver.disconnect();
        this.stopAutoplay();
      }

      bindButtons() {
        if (this.buttonsBound) return;
        this.buttonsBound = true;
        document.addEventListener('click', (event) => {
          const prev = event.target.closest(`[data-snk-slider-prev="${this.id}"]`);
          const next = event.target.closest(`[data-snk-slider-next="${this.id}"]`);
          if (prev || next) this.stopAutoplay();
          if (prev) this.go(-1);
          if (next) this.go(1);
        });
      }

      go(direction) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        this.track.scrollBy({ left: direction * this.track.clientWidth * 0.9, behavior });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const atStart = scrollLeft <= 2;
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
        document
          .querySelectorAll(`[data-snk-slider-prev="${this.id}"]`)
          .forEach((button) => (button.disabled = atStart));
        document
          .querySelectorAll(`[data-snk-slider-next="${this.id}"]`)
          .forEach((button) => (button.disabled = atEnd));

        const maxScroll = scrollWidth - clientWidth;
        if (this.progress) {
          const width = Math.max(12, (clientWidth / scrollWidth) * 100);
          this.progress.style.width = `${width}%`;
          this.progress.style.left = `${maxScroll > 0 ? (scrollLeft / maxScroll) * (100 - width) : 0}%`;
        }
        if (this.counter) {
          const items = this.track.children;
          const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : clientWidth;
          const index = atEnd ? items.length - 1 : Math.min(items.length - 1, Math.round(scrollLeft / (step || 1)));
          const pad = (n) => String(n).padStart(2, '0');
          this.counter.textContent = `${pad(index + 1)} / ${pad(items.length)}`;
        }

        if (!this.dots) return;
        const pages = clientWidth ? Math.max(1, Math.ceil(scrollWidth / clientWidth - 0.05)) : 1;
        if (this.dots.childElementCount !== pages) {
          this.dots.replaceChildren(...Array.from({ length: pages }, () => document.createElement('i')));
        }
        const current = atEnd ? pages - 1 : Math.round(scrollLeft / clientWidth);
        [...this.dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === current));
        this.dots.hidden = pages < 2;
      }
    }
  );
}
